// Typed wrapper over the broker product catalog (PMS-S141, BRK-S051):
// GET /api/broker/products returns the ACTIVE products with their full in-code
// manifests — the canonical field catalog, the indication/quote floors (derived
// server-side from the registered carriers' declared required fields), and the
// offer-terms display schema the comparison table renders. This replaces the
// hardcoded client product list (coverageLines.ts). Types are derived from the
// PMS contract (docs/openapi.yaml → brokerProductSchema), like the sibling
// seams (brokerAccounts.ts / brokerSubmissions.ts).

import type { AuthedRequester } from "@/lib/brokerAuth";

/** The PMS coverage-line enum — the `line` a submission is created with. */
export type LineOfBusiness =
  | "general_liability"
  | "workers_compensation"
  | "commercial_auto";

/** The registered product-manifest keys (PMS lib/products registry). */
export type ProductKey =
  | "workers_compensation"
  | "contractors_gl"
  | "cannabis_commercial_auto";

/** Where a canonical field lives: `submission` fields go in the submission's
 *  field bag (PMS-S142); `account` fields live on the Account — the manifest
 *  only declares them so quote-readiness can name what's missing. */
export type FieldScope = "account" | "submission";

/** The two quoting stages a floor is derived for: a rough indication vs a firm,
 *  bindable quote (which typically needs more of the insured profile). */
export type QuoteStage = "indication" | "quote";

/** Manifest field control types. `classCode` renders the class-code typeahead;
 *  `group` is a repeatable row set whose shape is the `item` array; `location`
 *  stores a Location id (a WC payroll row's rated location, PMS-S660). */
export type FieldType =
  | "text"
  | "number"
  | "currency"
  | "date"
  | "select"
  | "boolean"
  | "classCode"
  | "location"
  | "priorCarrier"
  | "group";

export interface FieldOption {
  value: string;
  label: string;
}

export type FieldDefault = string | number | boolean | string[];

/** A sub-field of a repeatable group row (no scope — it lives inside the
 *  group's rows). `optional` marks row fields the broker may leave blank. */
export interface GroupItemField {
  key: string;
  label: string;
  type: FieldType;
  helpText?: string;
  options?: FieldOption[];
  multiple?: boolean;
  defaultValue?: FieldDefault;
  optional?: boolean;
  min?: number;
  max?: number;
  integer?: boolean;
}

/** One canonical field in a product's catalog. Published flat with optional
 *  facets (options/multiple for selects, item/minItems for groups) — the shape
 *  of the PMS fieldDefSchema. */
/** Which step of a line's wizard asks a field (PMS-S703). Within a section
 *  the manifest's order is the order the wizard asks. */
export const FIELD_SECTIONS = [
  "wc_basic_information",
  "wc_general_information",
  "wc_history",
  "wc_coverage",
  "wc_schedule_rating",
] as const;
export type FieldSection = (typeof FIELD_SECTIONS)[number];

/** One answer a field waits on (PMS-S762). */
export interface ShowWhenCondition {
  key: string;
  equals: string | boolean;
}

/** When a field is asked: while an answer matches, or while any one of
 *  several does (PMS-S809). */
export type ShowWhen = ShowWhenCondition | { any: ShowWhenCondition[] };

/** The answers a condition reads, one or several. A condition of neither
 *  shape reads as none: a tolerant reader, as a newer PMS might send one. */
export function showWhenConditions(
  when: ShowWhen | undefined,
): ShowWhenCondition[] {
  if (!when) return [];
  if ("any" in when) return Array.isArray(when.any) ? when.any : [];
  return typeof when.key === "string" ? [when] : [];
}

/** Whether a field is asked by its own condition: always without one or with
 *  one it can't read (never hidden on a guess), else while any of its answers
 *  matches. */
export function showWhenMet(
  when: ShowWhen | undefined,
  answers: Readonly<Record<string, unknown>>,
): boolean {
  const conditions = showWhenConditions(when);
  return (
    conditions.length === 0 ||
    conditions.some((c) => answers[c.key] === c.equals)
  );
}

export interface ManifestField {
  key: string;
  label: string;
  scope: FieldScope;
  type: FieldType;
  /** For an ACORD question, the form's verbatim wording. */
  helpText?: string;
  options?: FieldOption[];
  multiple?: boolean;
  defaultValue?: FieldDefault;
  item?: GroupItemField[];
  minItems?: number;
  // `section`, `min` and `max` (PMS-S703) are held to the submissions slice,
  // which pins the catalog for the WC wizard (BRK-S304).
  /** The wizard step that asks it; absent on a PMS before PMS-S703. */
  section?: FieldSection;
  /** Bounds of a number or currency value, which the bag write enforces. */
  min?: number;
  max?: number;
  integer?: boolean;
  required?: boolean;
  showWhen?: ShowWhen;
  unit?: string;
  replacedBy?: string;
}

/** An ordered group of offer-term rows the comparison table renders; termKeys
 *  reference canonical field keys (labels/formats come from the field defs). */
export interface OfferTermsGroup {
  label: string;
  termKeys: string[];
}

/** A product's full manifest: field catalog + derived minimum-information
 *  floors per quoting stage + offer-terms display schema. */
export interface ProductManifest {
  key: ProductKey;
  label: string;
  lineOfBusiness: LineOfBusiness;
  fields: ManifestField[];
  offerTerms: OfferTermsGroup[];
  floors: {
    indication: string[];
    quote: string[];
    bind?: string[];
  };
  /** Every employers' liability triple the line writes, `[each accident,
   *  disease each employee, disease policy limit]`, ascending (PMS-S811).
   *  Workers' Comp only; absent before PMS-S811. */
  elLimitCombinations?: [number, number, number][];
}

/** One row of the catalog. `manifest` is null for a product with no registered
 *  manifest (the legacy single-carrier GL). */
export interface BrokerProduct {
  id: string;
  name: string;
  lineOfBusiness: LineOfBusiness;
  distributionMode: "single_carrier" | "marketplace";
  manifest: ProductManifest | null;
}

/** A canonical field reference (key + label + where it lives). Used for the
 *  structured quote-readiness / account-floor checklists (PMS-S144, BRK-S057),
 *  and the shape of the server's `details.missingFields`. Lives here — the
 *  manifest/canonical-field domain — so both the products/account floor logic
 *  and the submissions seam share one type without an import cycle. */
export interface MissingField {
  key: string;
  label: string;
  scope: FieldScope;
}

/** GET /api/broker/products — the active product catalog with manifests. */
export function getProducts(
  request: AuthedRequester,
): Promise<{ items: BrokerProduct[] }> {
  return request<{ items: BrokerProduct[] }>("/api/broker/products");
}

// ─── Floor keys (PMS-S660) ───────────────────────────────────────────────────
// A floor key is one of three things, mirrored from PMS `floorKeyDef`
// (lib/products/registry.ts):
// - a catalog field: `exposures`;
// - a group item every row must carry: `exposures.fullTimeCount`;
// - the county of every row's rated location: `exposures.locationId.county`.

/** The county of every row's rated location (`<group>.<item>.county`). */
export function isLocationCountyKey(key: string): boolean {
  const parts = key.split(".");
  return parts.length === 3 && parts[2] === "county";
}

/** PMS's label for the county floor key, so the checklist reads the same as a
 *  server 400 naming it. */
export const LOCATION_COUNTY_LABEL = "County of each rated location";

/**
 * The ACCOUNT-scope fields on a given stage's floor (BRK-S057) — the insured
 * Account fields that must be satisfied before that stage. Submission-scope
 * floor fields are captured in the manifest details step, not the account; the
 * account never carries them. Derived from the manifest the server already
 * serves (fields carry `scope`, floors carry stages) — no product conditionals.
 *
 * The county of each rated location is account-scoped too (PMS-S660): a WC
 * quote needs it, and it lives on the insured's address. It has no catalog
 * field of its own, so it comes back as one under the floor key itself.
 */
export function accountFloorFields(
  manifest: ProductManifest,
  stage: QuoteStage,
): ManifestField[] {
  const keys = new Set(manifest.floors[stage]);
  const fields = manifest.fields.filter(
    (f) => f.scope === "account" && keys.has(f.key),
  );
  const county = manifest.floors[stage].find(isLocationCountyKey);
  return county
    ? [
        ...fields,
        {
          key: county,
          label: LOCATION_COUNTY_LABEL,
          scope: "account",
          type: "text",
        },
      ]
    : fields;
}

/**
 * The manifest with every group item a floor requires made required
 * (PMS-S660). The manifest marks the employee counts `optional`, but a WC
 * quote's floor names them (`exposures.fullTimeCount`, `.partTimeCount`), so
 * the details form asks for them on every row up front — Nav, 2026-10-06:
 * "require employee counts for all WC". 0 is an answer.
 */
export function withFloorRequiredItems(
  manifest: ProductManifest,
): ProductManifest {
  const floorKeys = new Set([
    ...manifest.floors.indication,
    ...manifest.floors.quote,
  ]);
  return {
    ...manifest,
    fields: manifest.fields.map((f) =>
      f.type === "group" && f.item
        ? {
            ...f,
            item: f.item.map((i) =>
              i.optional && floorKeys.has(`${f.key}.${i.key}`)
                ? { ...i, optional: false }
                : i,
            ),
          }
        : f,
    ),
  };
}

/** Product-picker options for starting a submission: one option per PRODUCT,
 *  valued by its id (the create is product-addressed since PMS-S147 — a line
 *  can carry several products, so a line-keyed option would be ambiguous). */
export function productOptions(products: BrokerProduct[]): FieldOption[] {
  return products.map((p) => ({ value: p.id, label: p.name }));
}
